import {
  Briefcase,
  Home,
  MessageCircle,
  PanelLeft,
  PanelRight,
  Search,
  Settings,
  Users
} from 'lucide-react';
import React, { useState } from 'react';

/**
 * Modern Chat Sidebar Component
 * Based on conversation-mockup.html design with collapsible functionality
 *
 * @param {Object} props
 * @param {boolean} props.isCollapsed - Sidebar collapsed state
 * @param {Function} props.onToggle - Toggle sidebar handler
 * @param {string} props.activePath - Currently active route
 */
export function ChatSidebar({ isCollapsed = false, onToggle, activePath = '/conversation' }) {
  const [isVisible, setIsVisible] = useState(true);

  const toggleSidebar = () => {
    setIsVisible(!isVisible);
    onToggle && onToggle(!isVisible);
  };

  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/conversation', icon: MessageCircle, label: 'Conversation' },
    { path: '/people-search', icon: Search, label: 'People Search' },
    { path: '/contacts', icon: Users, label: 'Candidates' },
    { path: '/jobs', icon: Briefcase, label: 'Jobs' },
    { path: '/settings', icon: Settings, label: 'Settings' }
  ];

  const isActive = (path) => activePath === path;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={toggleSidebar}
        className={`
          fixed top-1/2 left-0 transform -translate-y-1/2 z-50
          bg-white border border-gray-300 rounded-r-lg px-2 py-3 shadow-lg
          hover:bg-gray-50 transition-all duration-300
          ${isVisible ? 'left-64' : 'left-0'}
        `}
      >
        {isVisible ? (
          <PanelLeft className="w-5 h-5 text-gray-600" />
        ) : (
          <PanelRight className="w-5 h-5 text-gray-600" />
        )}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 bottom-0 left-0 w-64 bg-white border-r border-gray-200 z-30
        transition-transform duration-300 ease-in-out
        ${isVisible ? 'translate-x-0' : '-translate-x-full'}
        hidden lg:flex lg:flex-col
      `}>
        {/* Logo Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 shrink-0">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#f7911d] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <span className="text-lg font-semibold text-gray-900">ALICE</span>
          </a>
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 overflow-y-auto py-4 space-y-1">
          {/* Dashboard */}
          <div className="px-3">
            <a
              href="/dashboard"
              className={`
                flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors
                ${isActive('/dashboard')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              <Home className="h-5 w-5" />
              <span className="flex-1">Dashboard</span>
            </a>
          </div>

          {/* Conversation - Active State */}
          <div className="px-3">
            <a
              href="/conversation"
              className={`
                flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors
                ${isActive('/conversation')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              <MessageCircle className="h-5 w-5" />
              <span className="flex-1">Conversation</span>
            </a>
          </div>

          {/* Heading */}
          <div className="px-4 pt-5 pb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              RECRUITING
            </span>
          </div>

          {/* People Search */}
          <div className="px-3">
            <a
              href="/people-search"
              className={`
                flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors
                ${isActive('/people-search')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              <Search className="h-5 w-5" />
              <span className="flex-1">People Search</span>
            </a>
          </div>

          {/* Candidates */}
          <div className="px-3">
            <a
              href="/contacts"
              className={`
                flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors
                ${isActive('/contacts')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              <Users className="h-5 w-5" />
              <span className="flex-1">Candidates</span>
            </a>
          </div>

          {/* Heading */}
          <div className="px-4 pt-5 pb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              MANAGEMENT
            </span>
          </div>

          {/* Jobs */}
          <div className="px-3">
            <a
              href="/jobs"
              className={`
                flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors
                ${isActive('/jobs')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              <Briefcase className="h-5 w-5" />
              <span className="flex-1">Jobs</span>
            </a>
          </div>

          {/* Settings */}
          <div className="px-3">
            <a
              href="/settings"
              className={`
                flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors
                ${isActive('/settings')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }
              `}
            >
              <Settings className="h-5 w-5" />
              <span className="flex-1">Settings</span>
            </a>
          </div>
        </div>

        {/* Credits Footer Widget */}
        <div className="px-4 py-4 border-t border-gray-200 shrink-0">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-xs font-medium text-gray-600">Credits</p>
                <p className="text-2xl font-bold text-blue-600">150</p>
              </div>
            </div>
            <a
              href="/billing"
              className="block text-center text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors mt-2"
            >
              Buy More →
            </a>
          </div>
        </div>
      </aside>
    </>
  );
}

export default ChatSidebar;

